import { baseCMSApi } from '@/shared/api/base-cms-api';
import { CMS_ENDPOINTS } from '@/shared/api/endpoints';
import type { OnboardingStep as OnboardingStepRaw } from '@/shared/cms/shared';

import type { OnboardingStep } from '../model/types/onboarding';

const onboardingApi = baseCMSApi
    .enhanceEndpoints({
        addTagTypes: ['OnboardingSteps', 'OnboardingStep'],
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getOnboardingSteps: build.query<
                OnboardingStep[],
                { locale: string }
            >({
                providesTags: ['OnboardingSteps'],
                query: (params) => ({
                    url: CMS_ENDPOINTS.getOnboardingSteps(),
                    method: 'GET',
                    params: {
                        locale: params.locale,
                        depth: 1,
                        limit: 20,
                        sort: '_order',
                    },
                }),
                transformResponse: (response: unknown): OnboardingStep[] => {
                    if (typeof response === 'object' && response !== null) {
                        const responseObj = response as Record<string, unknown>;

                        if (
                            Array.isArray(responseObj['docs']) &&
                            responseObj['docs'].length > 0
                        ) {
                            return responseObj['docs'].map(
                                (step: OnboardingStepRaw) =>
                                    ({
                                        id: step.id,
                                        title: step.title,
                                        slug: step.slug,
                                        subtitle: step.subtitle,
                                        // eslint-disable-next-line no-underscore-dangle
                                        order: step._order || 0,
                                    }) as OnboardingStep,
                            );
                        }

                        return [];
                    }

                    return [];
                },
            }),
            getOnboardingStepBySlug: build.query<
                OnboardingStep | null,
                { locale: string; slug: string }
            >({
                providesTags: ['OnboardingStep'],
                query: (params) => ({
                    url: CMS_ENDPOINTS.getOnboardingSteps(),
                    method: 'GET',
                    params: {
                        where: {
                            slug: {
                                equals: params.slug,
                            },
                        },
                        locale: params.locale,
                        depth: 1,
                        limit: 1,
                    },
                }),
                transformResponse: (
                    response: unknown,
                ): OnboardingStep | null => {
                    if (typeof response === 'object' && response !== null) {
                        const responseObj = response as Record<string, unknown>;

                        if (
                            Array.isArray(responseObj['docs']) &&
                            responseObj['docs'].length > 0
                        ) {
                            const step = responseObj[
                                'docs'
                            ][0] as OnboardingStepRaw;

                            return {
                                id: step.id,
                                title: step.title,
                                slug: step.slug,
                                subtitle: step.subtitle,
                                // eslint-disable-next-line no-underscore-dangle
                                order: step._order || 0,
                            } as OnboardingStep;
                        }

                        return null;
                    }

                    return null;
                },
            }),
        }),
    });

export const getOnboardingQuery =
    onboardingApi.endpoints.getOnboardingSteps.initiate;

export const { useGetOnboardingStepsQuery, useGetOnboardingStepBySlugQuery } =
    onboardingApi;

export const getOnboardingStepsFulfilledMatcher =
    onboardingApi.endpoints.getOnboardingSteps.matchFulfilled;
